import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Edit2, Trash2, Shield, Key, Search, 
  Filter, AlertCircle, CheckCircle, XCircle, Eye, EyeOff
} from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';
import { PageLayout, PageHeader, PageBody } from './PageLayout';

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDataAccessModal, setShowDataAccessModal] = useState(false);
  const [dataAccessUser, setDataAccessUser] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    customers: [],
    vendors: [],
    customer_account_managers: [],
    vendor_account_managers: []
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = getCurrentToken();
      
      const response = await apiCall('/api/auth/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setShowCreateModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = getCurrentToken();
      const response = await apiCall(`/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        loadUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleDataAccess = async (user) => {
    setDataAccessUser(user);
    
    try {
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/postgresql-hosts/${user.host_tenant_id}/data-filters/options`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
    
    setShowDataAccessModal(true);
  };

  const getRoleLabel = (role) => {
    const roleMap = {
      'admin': 'Admin',
      'host_admin': 'Host Admin',
      'host_user': 'Host User'
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      'admin': 'bg-purple-100 text-purple-800',
      'host_admin': 'bg-blue-100 text-blue-800',
      'host_user': 'bg-green-100 text-green-800'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  const canCreateRole = (roleToCreate) => {
    // Admin can create Host Admin
    if (user.role === 'admin' && roleToCreate === 'host_admin') return true;
    // Host Admin can create Host User
    if (user.role === 'host_admin' && roleToCreate === 'host_user') return true;
    return false;
  };

  const getAvailableRoles = () => {
    if (user.role === 'admin') {
      return [{ value: 'host_admin', label: 'Host Admin' }];
    } else if (user.role === 'host_admin') {
      return [{ value: 'host_user', label: 'Host User' }];
    }
    return [];
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.host_tenant_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && u.is_active) ||
                         (filterStatus === 'inactive' && !u.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <PageLayout>
      <PageHeader
        title="User Management"
        icon={Users}
        actions={
          getAvailableRoles().length > 0 && (
            <button
              onClick={handleCreateUser}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Add User</span>
            </button>
          )
        }
      />
      <PageBody padded={false} className="p-6">
      {/* Filters */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="host_admin">Host Admin</option>
          <option value="host_user">Host User</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Stats */}
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{users.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {users.filter(u => u.is_active).length}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Host Admins</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {users.filter(u => u.role === 'host_admin').length}
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Host Users</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {users.filter(u => u.role === 'host_user').length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Users className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Users className="h-16 w-16 text-gray-300 mb-4" />
              <p className="text-lg font-medium">No users found</p>
              <p className="text-sm mt-1">Try adjusting your filters or create a new user</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Host Tenant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{u.username}</div>
                            <div className="text-xs text-gray-500">{u.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{u.host_tenant_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(u.role)}`}>
                          {getRoleLabel(u.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.is_active ? (
                          <span className="flex items-center text-sm text-green-600">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center text-sm text-red-600">
                            <XCircle className="h-4 w-4 mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {(user.role === 'admin' && u.role === 'host_admin') || 
                           (user.role === 'host_admin' && u.role === 'host_user') ? (
                            <>
                              <button
                                onClick={() => handleEditUser(u)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Edit User"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDataAccess(u)}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                title="Manage Data Access"
                              >
                                <Shield className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.id, u.username)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          currentUser={user}
          availableRoles={getAvailableRoles()}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadUsers();
          }}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          currentUser={user}
          targetUser={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}

      {showDataAccessModal && (
        <DataAccessModal
          user={dataAccessUser}
          filterOptions={filterOptions}
          onClose={() => {
            setShowDataAccessModal(false);
            setDataAccessUser(null);
          }}
          onSuccess={() => {
            setShowDataAccessModal(false);
            setDataAccessUser(null);
            loadUsers();
          }}
        />
      )}
    </PageBody>
    </PageLayout>
  );
};

const CreateUserModal = ({ currentUser, availableRoles, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    host_tenant_id: currentUser.host_tenant_id,
    role: availableRoles[0]?.value || ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [hosts, setHosts] = useState([]);

  // Admin picks the tenant a new user belongs to; load existing hosts so the
  // field is a dropdown rather than free text. 'admin' (the platform tenant) is
  // always offered. Host admins can only create users in their own tenant, so
  // they don't need the list.
  useEffect(() => {
    if (currentUser.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      try {
        const token = getCurrentToken();
        const response = await apiCall('/api/postgresql-hosts', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setHosts(data.hosts || []);
      } catch (err) {
        console.error('Failed to load hosts for user form:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser.role]);

  // 'admin' + every existing host tenant, de-duplicated. The current value is
  // always kept selectable even if the list is stale or still loading.
  const hostOptions = (() => {
    const seen = new Set();
    const out = [];
    for (const id of ['admin', ...hosts.map((h) => h.host_tenant_id).filter(Boolean)]) {
      if (!seen.has(id)) { seen.add(id); out.push(id); }
    }
    if (formData.host_tenant_id && !seen.has(formData.host_tenant_id)) {
      out.push(formData.host_tenant_id);
    }
    return out;
  })();

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username) newErrors.username = 'Username is required';
    if (formData.username.length < 3) newErrors.username = 'Username must be at least 3 characters';
    
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.host_tenant_id) newErrors.host_tenant_id = 'Host tenant is required';
    if (!formData.role) newErrors.role = 'Role is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      const token = getCurrentToken();
      
      const response = await apiCall('/api/auth/users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          host_tenant_id: formData.host_tenant_id,
          role: formData.role
        })
      });
      
      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Create New User</h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.username ? 'border-red-500' : ''
                }`}
                placeholder="johndoe"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.confirmPassword ? 'border-red-500' : ''
                }`}
                placeholder="••••••••"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Host Tenant *
              </label>
              <select
                value={formData.host_tenant_id}
                onChange={(e) => setFormData({ ...formData, host_tenant_id: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.host_tenant_id ? 'border-red-500' : ''
                }`}
                disabled={currentUser.role !== 'admin'}
              >
                {hostOptions.map((id) => (
                  <option key={id} value={id}>
                    {id === 'admin' ? 'admin (platform)' : id}
                  </option>
                ))}
              </select>
              {errors.host_tenant_id && (
                <p className="mt-1 text-sm text-red-600">{errors.host_tenant_id}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.role ? 'border-red-500' : ''
                }`}
              >
                <option value="">Select Role</option>
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditUserModal = ({ currentUser, targetUser, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    is_active: targetUser.is_active
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.password) {
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      const token = getCurrentToken();
      
      const updates = {
        is_active: formData.is_active
      };
      
      if (formData.password) {
        updates.password = formData.password;
      }
      
      const response = await apiCall(`/api/auth/users/${targetUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Edit User: {targetUser.username}</h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password (leave blank to keep current)
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {formData.password && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : ''
                  }`}
                  placeholder="••••••••"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            )}

            {currentUser.role === 'admin' && targetUser.role !== 'admin' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Active User
                </label>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DataAccessModal = ({ user, filterOptions, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    data_access_mode: 'all',
    allowed_customers: [],
    allowed_vendors: [],
    allowed_customer_account_managers: [],
    allowed_vendor_account_managers: [],
    reason: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('customers');

  useEffect(() => {
    loadUserDataAccess();
  }, [user]);

  const loadUserDataAccess = async () => {
    try {
      setLoading(true);
      const token = getCurrentToken();
      const response = await apiCall(`/api/auth/users/${user.id}/data-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          data_access_mode: data.data_access_mode || 'all',
          allowed_customers: data.allowed_customers || [],
          allowed_vendors: data.allowed_vendors || [],
          allowed_customer_account_managers: data.allowed_customer_account_managers || [],
          allowed_vendor_account_managers: data.allowed_vendor_account_managers || [],
          reason: ''
        });
      }
    } catch (error) {
      console.error('Error loading data access:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const token = getCurrentToken();
      
      const response = await apiCall(`/api/auth/users/${user.id}/data-access`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update data access');
      }
    } catch (error) {
      console.error('Error updating data access:', error);
      alert('Failed to update data access');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleItem = (category, item) => {
    const current = formData[category];
    const newValue = current.includes(item)
      ? current.filter(i => i !== item)
      : [...current, item];
    
    setFormData({ ...formData, [category]: newValue });
  };

  const handleSelectAll = (category, items) => {
    setFormData({ ...formData, [category]: items });
  };

  const handleClearAll = (category) => {
    setFormData({ ...formData, [category]: [] });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Data Access Control</h2>
              <p className="text-sm text-gray-600 mt-1">
                User: {user.username} ({user.host_tenant_id})
              </p>
            </div>
            <Shield className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {/* Access Mode Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Mode
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="all"
                    checked={formData.data_access_mode === 'all'}
                    onChange={(e) => setFormData({ ...formData, data_access_mode: e.target.value })}
                    className="mr-2"
                  />
                  <span>Unrestricted (All Data)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="restricted"
                    checked={formData.data_access_mode === 'restricted'}
                    onChange={(e) => setFormData({ ...formData, data_access_mode: e.target.value })}
                    className="mr-2"
                  />
                  <span>Restricted (Filtered)</span>
                </label>
              </div>
            </div>

            {/* Filters - Only show if restricted mode */}
            {formData.data_access_mode === 'restricted' && (
              <div className="space-y-4">
                {/* Tabs */}
                <div className="border-b">
                  <div className="flex space-x-4">
                    {[
                      { key: 'customers', label: 'Customers' },
                      { key: 'vendors', label: 'Vendors' },
                      { key: 'customer_account_managers', label: 'Customer AMs' },
                      { key: 'vendor_account_managers', label: 'Vendor AMs' }
                    ].map(tab => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2 border-b-2 transition-colors ${
                          activeTab === tab.key
                            ? 'border-purple-600 text-purple-600 font-medium'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {tab.label}
                        <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded-full">
                          {formData[`allowed_${tab.key}`].length}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="bg-gray-50 rounded-lg p-4">
                  {activeTab === 'customers' && (
                    <FilterList
                      title="Allowed Customers"
                      items={filterOptions.customers.map(c => ({ value: c.code, label: `${c.name} (${c.code})` }))}
                      selected={formData.allowed_customers}
                      onToggle={(item) => handleToggleItem('allowed_customers', item)}
                      onSelectAll={() => handleSelectAll('allowed_customers', filterOptions.customers.map(c => c.code))}
                      onClearAll={() => handleClearAll('allowed_customers')}
                    />
                  )}

                  {activeTab === 'vendors' && (
                    <FilterList
                      title="Allowed Vendors"
                      items={filterOptions.vendors.map(v => ({ value: v.code, label: `${v.name} (${v.code})` }))}
                      selected={formData.allowed_vendors}
                      onToggle={(item) => handleToggleItem('allowed_vendors', item)}
                      onSelectAll={() => handleSelectAll('allowed_vendors', filterOptions.vendors.map(v => v.code))}
                      onClearAll={() => handleClearAll('allowed_vendors')}
                    />
                  )}

                  {activeTab === 'customer_account_managers' && (
                    <FilterList
                      title="Allowed Customer Account Managers"
                      items={filterOptions.customer_account_managers.map(am => ({ value: am, label: am }))}
                      selected={formData.allowed_customer_account_managers}
                      onToggle={(item) => handleToggleItem('allowed_customer_account_managers', item)}
                      onSelectAll={() => handleSelectAll('allowed_customer_account_managers', filterOptions.customer_account_managers)}
                      onClearAll={() => handleClearAll('allowed_customer_account_managers')}
                    />
                  )}

                  {activeTab === 'vendor_account_managers' && (
                    <FilterList
                      title="Allowed Vendor Account Managers"
                      items={filterOptions.vendor_account_managers.map(am => ({ value: am, label: am }))}
                      selected={formData.allowed_vendor_account_managers}
                      onToggle={(item) => handleToggleItem('allowed_vendor_account_managers', item)}
                      onSelectAll={() => handleSelectAll('allowed_vendor_account_managers', filterOptions.vendor_account_managers)}
                      onClearAll={() => handleClearAll('allowed_vendor_account_managers')}
                    />
                  )}
                </div>

                {/* Reason field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Change (Optional)
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Explain why this data access change is being made..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FilterList = ({ title, items, selected, onToggle, onSelectAll, onClearAll }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = items.filter(item =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900">{title}</h3>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onSelectAll}
            className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="mb-3">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="max-h-60 overflow-y-auto border rounded-lg bg-white">
        {filteredItems.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No items found</div>
        ) : (
          <div className="divide-y">
            {filteredItems.map((item) => (
              <label
                key={item.value}
                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(item.value)}
                  onChange={() => onToggle(item.value)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm text-gray-900">{item.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="mt-2 text-xs text-gray-600">
        {selected.length} of {items.length} selected
      </div>
    </div>
  );
};

export default UserManagement;